import '@lib/ce-polyfill';
import motion from '@lib/motion';
import { listen, dispatch } from '@theme/events';
import { SKY_THEME_EVENTS } from '@theme/config';
import { ResizeManager } from '@theme/event-manager';

let resizeTimer;
window.addEventListener('DOMContentLoaded', () => {
  document.body.classList.add('loaded');
  document.dispatchEvent(new CustomEvent('page:loaded'));
});

class DynamicGrid {
  static SELECTOR = '[data-editor-element="true"]';
  static CLASS = 'editor-is-selected';

  constructor() {
    if (!window.Shopify?.designMode) return;

    this.onSelect = this.onSelect.bind(this);
    this.onDeselect = this.onDeselect.bind(this);
    this.init();
  }

  init() {
    document.addEventListener('shopify:section:select', this.onSelect);
    document.addEventListener('shopify:section:deselect', this.onDeselect);
    document.addEventListener('shopify:block:select', this.onSelect);
    document.addEventListener('shopify:block:deselect', this.onDeselect);
  }

  getSection(event) {
    try {
      const sectionId = event?.detail?.sectionId;
      if (sectionId) {
        const byId = document.getElementById(`shopify-section-${sectionId}`);
        if (byId) return byId;
      }

      const target = event?.target;
      if (!target) return null;
      if (target.classList?.contains('shopify-section')) return target;
      return target.closest?.('.shopify-section') || null;
    } catch (_) {
      return null;
    }
  }

  getEditorElements(section) {
    if (!section) return [];

    try {
      const elements = [];
      if (section.matches?.(DynamicGrid.SELECTOR)) elements.push(section);
      if (section.querySelectorAll) elements.push(...section.querySelectorAll(DynamicGrid.SELECTOR));
      return elements;
    } catch (_) {
      return [];
    }
  }

  setSelected(section, selected) {
    this.getEditorElements(section).forEach((element) => {
      try {
        if (selected) element.classList.add(DynamicGrid.CLASS);
        else element.classList.remove(DynamicGrid.CLASS);
      } catch (_) { }
    });
  }

  clearOthers(activeSection) {
    try {
      document.querySelectorAll(DynamicGrid.SELECTOR).forEach((element) => {
        if (activeSection?.contains(element)) return;
        element.classList.remove(DynamicGrid.CLASS);
      });
    } catch (_) { }
  }

  onSelect(event) {
    try {
      const section = this.getSection(event);
      this.clearOthers(section);
      this.setSelected(section, true);
    } catch (_) { }
  }

  onDeselect(event) {
    try {
      this.setSelected(this.getSection(event), false);
    } catch (_) { }
  }
}
new DynamicGrid();

function isElementTabbable(element) {
  if (!(element instanceof Element)) return false;
  if (element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true') return false;
  if (element.closest('[data-a11y-disabled]')) return false;
  if (element.closest('[hidden]')) return false;
  if (element.closest('[aria-hidden="true"]')) return false;
  // Avoid checkVisibilityCSS — opacity:0 animated items inside drawers would be
  // excluded and break Tab focus traps while the drawer is open.
  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

const FOCUSABLE_SELECTOR =
  "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe";

const PRODUCT_CARD_FOCUS_SCOPE =
  '.product-card, .product-card-inline, .horizontal-product-card, .expandable-product-card, [data-horizontal-product-card]';

export function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isElementTabbable);
}

/**
 * Next tab stop after `anchor` in document order.
 * Prefers the next control inside the same product card (including hover-revealed
 * actions), then the next page focusable after that card/anchor.
 * @param {Element|null} anchor
 * @param {{ exclude?: Element|null }} [options]
 * @returns {Element|null}
 */
export function getNextFocusableAfter(anchor, options = {}) {
  if (!anchor || !document.contains(anchor)) return null;

  const excludeRoot = options.exclude || null;
  const card = typeof anchor.closest === 'function' ? anchor.closest(PRODUCT_CARD_FOCUS_SCOPE) : null;

  const isCandidate = (el) => {
    if (!(el instanceof Element) || el === anchor) return false;
    if (excludeRoot?.contains?.(el)) return false;
    if (el.matches(':disabled') || el.getAttribute('aria-disabled') === 'true') return false;
    if (el.closest('[data-a11y-disabled]')) return false;
    if (el.closest('[hidden]')) return false;

    const ariaHiddenRoot = el.closest('[aria-hidden="true"]');
    if (ariaHiddenRoot && !card?.contains(el)) return false;

    try {
      const style = window.getComputedStyle(el);
      if (style.display === 'none') return false;
      // Hover-only card controls stay in the in-card sequence; restoreFocusTo reveals them.
      if (style.visibility === 'hidden' && (!card || !card.contains(el))) return false;
    } catch (_) {
      return false;
    }

    return true;
  };

  const collectIn = (root) => {
    if (!root) return [];
    const list = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => el === anchor || isCandidate(el),
    );
    if (list.includes(anchor)) return list;

    // Anchor may be visibility:hidden (QV) — still keep document order inside the card.
    let insertAt = list.length;
    for (let i = 0; i < list.length; i++) {
      if (anchor.compareDocumentPosition(list[i]) & Node.DOCUMENT_POSITION_FOLLOWING) {
        insertAt = i;
        break;
      }
    }
    list.splice(insertAt, 0, anchor);
    return list;
  };

  if (card) {
    const inCard = collectIn(card);
    const index = inCard.indexOf(anchor);
    if (index >= 0 && index < inCard.length - 1) return inCard[index + 1];
  }

  const scope = card || anchor;
  const pageList = Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isCandidate);

  for (const el of pageList) {
    if (card?.contains(el)) continue;
    const position = scope.compareDocumentPosition(el);
    if (!(position & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
    if (position & Node.DOCUMENT_POSITION_CONTAINED_BY) continue;
    return el;
  }

  return null;
}

export class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

const trapFocusHandlers = {};
/** When a modal/drawer is open, focus is trapped in this container. Cleared on removeTrapFocus. */
let currentTrapContainer = null;
/**
 * Stack of focus traps for nested overlays (e.g. quick view → pickup drawer).
 * Each entry: { container, returnTo }.
 * Default trapFocus replaces the stack (legacy). Pass { push: true } to nest.
 */
const focusTrapStack = [];

function detachTrapFocusListeners() {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
}

function captureReturnFocusTarget(container) {
  const active = document.activeElement;
  if (!active || active === document.body || active === document.documentElement) return null;
  if (typeof active.focus !== 'function') return null;
  // Prefer returning outside the new trap; inside usually means we already moved focus
  if (container?.contains?.(active)) return null;
  return active;
}

function safeFocus(element) {
  if (!element || typeof element.focus !== 'function') return false;
  if (!document.contains(element)) return false;
  try {
    element.focus({ preventScroll: true });
  } catch (_) {
    try {
      element.focus();
    } catch (_) {
      return false;
    }
  }
  return document.activeElement === element || element.contains?.(document.activeElement);
}

/**
 * Product-card QV / choose-options are visibility:hidden until hover.
 * Class alone is not enough in the same tick — force a reflow (and optional
 * inline overrides) so Chrome will accept focus() before closeDrawer blur().
 */
function revealProductCardControl(element) {
  if (!element) return null;
  const card = element.closest?.('.product-card, .product-card-inline');
  if (card) {
    card.classList.add('is-focus-returning');
    // Flush layout so visibility:visible from is-focus-returning applies now
    void card.offsetHeight;
  }

  // Choose-options lives under .product-card-actions which itself is visibility:hidden
  // until hover — children cannot override a hidden ancestor.
  const actions = element.closest?.('.product-card-actions');
  if (actions) {
    actions.style.setProperty('visibility', 'visible', 'important');
    actions.style.setProperty('opacity', '1', 'important');
    actions.style.setProperty('pointer-events', 'auto', 'important');
    actions.style.setProperty('transform', 'none', 'important');
    void actions.offsetHeight;
  }

  // Belt-and-suspenders: QV icon / style-3 button also hide themselves
  element.style.setProperty('visibility', 'visible', 'important');
  element.style.setProperty('opacity', '1', 'important');
  element.style.setProperty('pointer-events', 'auto', 'important');
  element.style.setProperty('transform', 'none', 'important');
  void element.offsetHeight;

  return card;
}

function clearProductCardControlReveal(element, card) {
  if (element) {
    const actions = element.closest?.('.product-card-actions');
    if (actions) {
      actions.style.removeProperty('visibility');
      actions.style.removeProperty('opacity');
      actions.style.removeProperty('pointer-events');
      actions.style.removeProperty('transform');
    }
    element.style.removeProperty('visibility');
    element.style.removeProperty('opacity');
    element.style.removeProperty('pointer-events');
    element.style.removeProperty('transform');
  }
  card?.classList.remove('is-focus-returning');
}

/**
 * Return keyboard focus to a target that may be visibility:hidden until hover
 * (e.g. product-card quick view / choose options). Reveals synchronously, focuses,
 * then relies on :focus-within to keep the control visible.
 * @returns {boolean} true only when focus actually landed on the target (or fallback)
 */
export function restoreFocusTo(element) {
  if (!element || typeof element.focus !== 'function') return false;
  if (!document.contains(element)) return false;

  let focusTarget = element;
  const card = revealProductCardControl(element);

  try {
    // After reveal: only fall back when the control is truly not rendered (e.g. mobile hide)
    if (window.getComputedStyle(element).display === 'none') {
      focusTarget =
        card?.querySelector(
          'a.product-card-heading, a.full-unstyled-link, .product-card-title a, a[href]:not([tabindex="-1"])',
        ) || element;
      if (focusTarget !== element) {
        clearProductCardControlReveal(element, null);
        revealProductCardControl(focusTarget);
      }
    }
  } catch (_) { }

  if (!focusTarget || !document.contains(focusTarget)) {
    clearProductCardControlReveal(element, card);
    return false;
  }

  const clearReveal = () => {
    clearProductCardControlReveal(element, card);
    if (focusTarget !== element) clearProductCardControlReveal(focusTarget, null);
  };

  if (safeFocus(focusTarget)) {
    // Keep reveal until :focus-within paints, then drop inline overrides
    requestAnimationFrame(() => {
      requestAnimationFrame(clearReveal);
    });
    return true;
  }

  // One forced retry after style flush
  requestAnimationFrame(() => {
    if (card) {
      card.classList.add('is-focus-returning');
      void card.offsetHeight;
    }
    if (safeFocus(focusTarget)) {
      requestAnimationFrame(clearReveal);
      return;
    }
    // Last resort: product title link so we never leave focus on body/chrome
    const fallback =
      card?.querySelector(
        'a.product-card-heading, a.full-unstyled-link, .product-card-title a, a[href]:not([tabindex="-1"])',
      ) || null;
    if (fallback && fallback !== focusTarget && safeFocus(fallback)) {
      clearReveal();
      return;
    }
    clearReveal();
  });

  return false;
}

function attachTrapFocusListeners(container) {
  const getEdges = () => {
    const elements = getFocusableElements(container);
    return {
      first: elements[0],
      last: elements[elements.length - 1],
    };
  };

  trapFocusHandlers.focusin = (event) => {
    if (container.contains(event.target)) return;
    const { first } = getEdges();
    (first || container).focus();
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    const { first, last } = getEdges();
    if (!first || !last) return;

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
      return;
    }

    // On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusin', trapFocusHandlers.focusin);
  document.addEventListener('keydown', trapFocusHandlers.keydown);
}

/**
 * Trap Tab focus inside a container.
 * @param {Element} container
 * @param {Element} [elementToFocus]
 * @param {{ push?: boolean }} [options] - `{ push: true }` nests on top of an existing trap
 *   (pickup over quick view, size chart over product drawer). Default replaces (legacy).
 */
export function trapFocus(container, elementToFocus = container, options = {}) {
  if (!container) return;

  const push = options?.push === true;
  const existingIndex = focusTrapStack.findIndex((entry) => entry.container === container);

  // Parent overlay tried to re-trap while a nested overlay is still open — ignore
  if (existingIndex !== -1 && existingIndex < focusTrapStack.length - 1) {
    return;
  }

  detachTrapFocusListeners();

  const isTop = existingIndex !== -1 && existingIndex === focusTrapStack.length - 1;

  if (isTop) {
    // Same container re-trap (e.g. quick-view delayed focus) — keep saved returnTo
  } else if (push) {
    focusTrapStack.push({
      container,
      returnTo: captureReturnFocusTarget(container),
    });
  } else {
    // Legacy: replace any active trap without restoring focus (old removeTrapFocus() call)
    focusTrapStack.length = 0;
    focusTrapStack.push({
      container,
      returnTo: captureReturnFocusTarget(container),
    });
  }

  currentTrapContainer = container;
  attachTrapFocusListeners(container);

  const focusTarget = elementToFocus || container;
  focusTarget.focus();

  if (
    focusTarget.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(focusTarget.type) &&
    focusTarget.value
  ) {
    focusTarget.setSelectionRange(0, focusTarget.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

export function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

export function removeTrapFocus(elementToFocus = null, options = {}) {
  detachTrapFocusListeners();

  const entry = focusTrapStack.length ? focusTrapStack.pop() : null;

  const prev = focusTrapStack.length ? focusTrapStack[focusTrapStack.length - 1] : null;
  currentTrapContainer = prev?.container ?? null;

  if (prev?.container) {
    attachTrapFocusListeners(prev.container);
  }

  // Prefer an explicit return target. If the caller passed the trap container itself
  // (legacy pickup/size-chart), use the saved opener instead.
  let focusTarget = null;
  if (elementToFocus && typeof elementToFocus.focus === 'function') {
    if (entry && elementToFocus === entry.container && entry.returnTo) {
      focusTarget = entry.returnTo;
    } else {
      focusTarget = elementToFocus;
    }
  } else if (entry?.returnTo) {
    focusTarget = entry.returnTo;
  }

  // ATC→sticky/drawer will move focus next; skip so we don't flash the opener
  if (options?.restoreFocus === false) return;

  restoreFocusTo(focusTarget);
}

export async function copyTextToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      console.info(e);
    }
  }

  try {
    window.prompt('Copy this:', text);
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Global focus manager: when focus moves via keyboard (Tab), scroll the focused element
 * into view smoothly — only when it is not already comfortably visible. Uses Lenis if
 * active, else native scrollIntoView. Respects prefers-reduced-motion.
 *
 * Note: the browser often does a minimal focus scroll first (leaving the target near an
 * edge, sometimes 10–20px clipped). A padded "comfortable" check still runs our smooth
 * center scroll in that case, while skipping jumps for items already well inside the view.
 */
(function initSmoothScrollOnFocus() {
  let focusMovedByKeyboard = false;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** Breathing room from viewport edges; also catches browser's edge-peek focus scroll. */
  const VIEW_MARGIN = 24;

  function getStickyHeaderOffset() {
    const header = document.querySelector('header[is="sticky-header"], .header[is="sticky-header"]');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    // Only count when the header is stuck over the viewport top
    if (rect.bottom <= 0 || rect.top > 1) return 0;
    return Math.max(0, Math.round(rect.bottom));
  }

  function getScrollY() {
    if (window.lenis && typeof window.lenis.scroll === 'number') return window.lenis.scroll;
    return window.scrollY || window.pageYOffset || 0;
  }

  /**
   * True when the element sits fully inside the padded visible area (below sticky header).
   * Near-edge / partially clipped / under-header targets return false so we still scroll.
   */
  function isComfortablyInView(el) {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    const headerOffset = getStickyHeaderOffset();
    const topBound = headerOffset + VIEW_MARGIN;
    const bottomBound = window.innerHeight - VIEW_MARGIN;
    const leftBound = VIEW_MARGIN;
    const rightBound = window.innerWidth - VIEW_MARGIN;
    const visibleH = Math.max(0, bottomBound - topBound);
    const visibleW = Math.max(0, rightBound - leftBound);

    const topOk =
      rect.height > visibleH
        ? rect.top <= topBound && rect.bottom >= bottomBound
        : rect.top >= topBound && rect.bottom <= bottomBound;
    const leftOk =
      rect.width > visibleW
        ? rect.left <= leftBound && rect.right >= rightBound
        : rect.left >= leftBound && rect.right <= rightBound;

    return topOk && leftOk;
  }

  function scrollFocusedElementIntoView(el) {
    const rect = el.getBoundingClientRect();
    const headerOffset = getStickyHeaderOffset();
    const visibleCenter = headerOffset + (window.innerHeight - headerOffset) / 2;
    const elementCenter = rect.top + rect.height / 2;
    const targetScrollY = Math.max(0, getScrollY() + elementCenter - visibleCenter);

    if (reducedMotion.matches) {
      el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      return;
    }

    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      window.lenis.scrollTo(targetScrollY, { duration: 0.8, immediate: false });
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.code?.toUpperCase() === 'TAB') focusMovedByKeyboard = true;
    },
    true,
  );
  document.addEventListener('mousedown', () => (focusMovedByKeyboard = false), true);
  document.addEventListener('touchstart', () => (focusMovedByKeyboard = false), true);

  document.addEventListener(
    'focusin',
    (e) => {
      if (!focusMovedByKeyboard) return;
      const el = e.target;
      if (!el || el === document.body || typeof el.getBoundingClientRect !== 'function') {
        focusMovedByKeyboard = false;
        return;
      }
      focusMovedByKeyboard = false;

      // Drawer/modal: facets.js owns nested body scroll. Never Lenis/page-scroll here.
      if (currentTrapContainer?.contains(el)) return;
      if (el.closest?.('.drawer[active], .drawer[open], [aria-modal="true"][open]')) return;

      const doScroll = () => {
        // Well inside the viewport already — no centering jump
        if (isComfortablyInView(el)) return;
        scrollFocusedElementIntoView(el);
      };

      requestAnimationFrame(() => requestAnimationFrame(doScroll));
    },
    true,
  );
})();

export function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class SkyQtyControl extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = listen(SKY_THEME_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const previousValue = this.input.value;
    if (button.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && button.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector("button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector("button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('sky-qty-control', SkyQtyControl);

/**
 * Global layout recalc scheduler.
 *
 * Runs registered callbacks in two passes to avoid cross-component layout thrashing:
 * 1. 'measure' — read geometry / cache values only
 * 2. 'apply' — write styles / DOM from cached values only
 *
 * Direct calls (no phase arg) should run both passes locally for backward compatibility.
 * Callbacks that cannot split yet must no-op on phase === 'measure' so they do not run twice.
 *
 * register(cb) is eager (header, announcement, scrollbar).
 * register(cb, { root: element }) waits until `root` is within 300px of the viewport
 * before joining measure/apply — so boot `_onRecalc` does not layout every section.
 */
class LayoutRecalcManager {
  constructor() {
    this.callbacks = new Set();
    this._activated = new Set();
    this._lazyRoots = new Map();
    this._lazyTargets = new WeakMap();
    this._lazyObserver = null;
    this._pendingOnly = new Set();
    this._runAllPending = false;
    this.scheduled = false;
    this.isMuted = false;
    this.phase = null;
    /** Suppresses body ResizeObserver while/after our own layout writes settle. */
    this.ignoreObserver = false;
  }

  _ensureLazyObserver() {
    if (this._lazyObserver || !('IntersectionObserver' in window)) return;

    this._lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const cbs = this._lazyTargets.get(entry.target);
          if (!cbs) return;

          cbs.forEach((cb) => this._activate(cb, true));
          this._lazyObserver.unobserve(entry.target);
          this._lazyTargets.delete(entry.target);
        });
      },
      { rootMargin: '300px 0px', threshold: 0 },
    );
  }

  _observeLazy(cb, root) {
    if (!('IntersectionObserver' in window)) {
      this._activate(cb);
      return;
    }

    this._ensureLazyObserver();

    let cbs = this._lazyTargets.get(root);
    if (!cbs) {
      cbs = new Set();
      this._lazyTargets.set(root, cbs);
      this._lazyObserver.observe(root);
    }
    cbs.add(cb);
  }

  _detachLazy(cb) {
    const root = this._lazyRoots.get(cb);
    this._lazyRoots.delete(cb);
    if (!root) return;

    const cbs = this._lazyTargets.get(root);
    if (!cbs) return;

    cbs.delete(cb);
    if (cbs.size) return;

    this._lazyObserver?.unobserve(root);
    this._lazyTargets.delete(root);
  }

  _activate(cb, requestAfter = false) {
    if (!this.callbacks.has(cb)) return;
    if (this._activated.has(cb)) return;

    this._activated.add(cb);
    this._detachLazy(cb);

    if (requestAfter) this.request(cb);
  }

  register(cb, options = {}) {
    if (typeof cb !== 'function') return;
    this.callbacks.add(cb);

    if (this._activated.has(cb)) return;

    const root = options.root;
    const eager = options.eager === true || !(root instanceof Element) || Boolean(window.Shopify?.designMode);

    if (eager) {
      this._activated.add(cb);
      return;
    }

    this._lazyRoots.set(cb, root);
    this._observeLazy(cb, root);
  }

  unregister(cb) {
    this.callbacks.delete(cb);
    this._activated.delete(cb);
    this._detachLazy(cb);
  }

  _runPhase(phase, callbacks) {
    this.phase = phase;
    callbacks.forEach((cb) => {
      if (!this.callbacks.has(cb)) return;
      try {
        cb(phase);
      } catch (error) {
        console.error('[layoutRecalcManager]', cb.name || 'anonymous', error);
      }
    });
  }

  request(cb) {
    if (this.isMuted) return;

    if (cb) {
      if (!this._activated.has(cb)) return;
      if (!this._runAllPending) this._pendingOnly.add(cb);
    } else {
      this._runAllPending = true;
      this._pendingOnly.clear();
    }

    if (this.scheduled) return;

    this.scheduled = true;

    requestAnimationFrame(() => {
      const runAll = this._runAllPending;
      const only = this._pendingOnly;
      this._runAllPending = false;
      this._pendingOnly = new Set();

      const list = runAll ? this._activated : only;

      this._runPhase('measure', list);

      // Apply writes (CSS vars on body, heights, etc.) can notify body ResizeObserver.
      // Ignore that feedback for this frame + the next so we do not recalc in a loop.
      this.ignoreObserver = true;
      this._runPhase('apply', list);
      this.phase = null;
      this.scheduled = false;

      requestAnimationFrame(() => {
        this.ignoreObserver = false;
      });
    });
  }
}

/** True when callback should read/cache geometry (manager measure pass or direct call). */
export function layoutPhaseMeasure(phase) {
  return phase == null || phase === 'measure';
}

/** True when callback should write styles/DOM (manager apply pass or direct call). */
export function layoutPhaseApply(phase) {
  return phase == null || phase === 'apply';
}

export const layoutRecalcManager = new LayoutRecalcManager();

class ScrollbarWidth {
  constructor() {
    this._width = null;
    this._pendingWidth = null;
    this._onRecalc = this._onRecalc.bind(this);
  }

  _onRecalc(phase) {
    if (layoutPhaseMeasure(phase)) {
      this._pendingWidth = window.innerWidth - document.documentElement.clientWidth;
    }

    if (layoutPhaseApply(phase)) {
      const width = this._pendingWidth ?? 0;
      if (width === this._width) return;
      this._width = width;
      document.documentElement.style.setProperty('--scrollbarWidth', `${width}px`);
    }
  }
}

const scrollbarWidth = new ScrollbarWidth();
layoutRecalcManager.register(scrollbarWidth._onRecalc);

/** Keep in sync with ResizeManager debounce in event-manager.js */
const LAYOUT_RECALC_DEBOUNCE_MS = 150;
const requestGlobalLayoutRecalc = debounce(() => layoutRecalcManager.request(), LAYOUT_RECALC_DEBOUNCE_MS);

/**
 * Boot used to fire layout recalc 2–3 times in quick succession:
 * - page:loaded (DOMContentLoaded)
 * - window load
 * - body ResizeObserver reacting to early CSS-var / class writes
 *
 * Keep a single post-load bootstrap. Components that need earlier measure
 * already call layoutRecalcManager.request() themselves.
 */
let hasBootstrappedLayout = false;

/**
 * Runs a callback once the Largest Contentful Paint has been observed.
 * Helps keep geometry reads out of the critical rendering window.
 */
if (typeof window.skyAfterLCP !== 'function') {
  window.skyAfterLCP = (cb) => {
    if (typeof cb !== 'function') return;

    if (window.__skyLcpResolved === true) {
      requestAnimationFrame(cb);
      return;
    }

    window.__skyLcpQueue = window.__skyLcpQueue || [];
    window.__skyLcpQueue.push(cb);

    if (window.__skyLcpObserverStarted) return;
    window.__skyLcpObserverStarted = true;

    const resolve = () => {
      if (window.__skyLcpResolved === true) return;
      window.__skyLcpResolved = true;
      const q = window.__skyLcpQueue || [];
      window.__skyLcpQueue = [];
      q.forEach((fn) => {
        try {
          fn();
        } catch (e) { }
      });
    };

    try {
      const existing = performance.getEntriesByType?.('largest-contentful-paint') || [];
      if (existing.length) {
        resolve();
        return;
      }

      if (!('PerformanceObserver' in window)) {
        // Fallback: don't block if API isn't available.
        setTimeout(resolve, 2500);
        return;
      }

      const obs = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            obs.disconnect();
            resolve();
            break;
          }
        }
      });

      obs.observe({ type: 'largest-contentful-paint', buffered: true });
      // Safety fallback: ensure we always resolve within a reasonable window.
      setTimeout(() => {
        obs.disconnect();
        resolve();
      }, 4000);
    } catch (_) {
      setTimeout(resolve, 2500);
    }
  };
}

function bootstrapGlobalLayoutRecalc() {
  if (hasBootstrappedLayout) return;
  hasBootstrappedLayout = true;
  // Avoid early forced layouts on first load; wait until LCP so layout is less "dirty".
  // This specifically prevents ScrollbarWidth's `clientWidth` read from showing up in
  // your Forced Reflow attribution during the critical rendering window.
  if (typeof window.skyAfterLCP === 'function') {
    window.skyAfterLCP(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => layoutRecalcManager.request(), { timeout: 2000 });
      } else {
        setTimeout(() => layoutRecalcManager.request(), 200);
      }
    });
  } else {
    // Fallback: still defer slightly to avoid blocking paint.
    requestAnimationFrame(() => layoutRecalcManager.request());
  }
}

if (document.readyState === 'complete') {
  bootstrapGlobalLayoutRecalc();
} else {
  window.addEventListener('load', bootstrapGlobalLayoutRecalc, { once: true });
}

window.addEventListener('resize', requestGlobalLayoutRecalc, { passive: true });
document.addEventListener('shopify:section:load', () => layoutRecalcManager.request());
document.addEventListener('shopify:section:reorder', () => layoutRecalcManager.request());

if (window.ResizeObserver) {
  const rootResizeObserver = new ResizeObserver(() => {
    // Avoid boot-time thrash before the single load bootstrap.
    if (!hasBootstrappedLayout) return;
    // Ignore ResizeObserver feedback caused by our own apply-phase writes.
    if (layoutRecalcManager.ignoreObserver) return;
    if (layoutRecalcManager.phase != null) return;
    requestGlobalLayoutRecalc();
  });

  const observeRoot = () => {
    if (!document.body) return;
    rootResizeObserver.observe(document.body);
  };

  if (document.body) {
    observeRoot();
  } else {
    document.addEventListener('DOMContentLoaded', observeRoot, { once: true });
  }
}

export function THEME_ERROR_ICON() {
  const label = window.accessibilityStrings?.error || 'Error';
  return `<svg class="icon-error icon-xsmall" role="img" aria-label="${label}" viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="10" fill="rgb(var(--errorColor))"/><path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5" stroke="rgb(var(--errorBackground))" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>`;
}

export function THEME_SUCCESS_ICON() {
  const label = window.accessibilityStrings?.success || 'Success';
  return `<svg class="icon-success icon-xsmall" role="img" aria-label="${label}" viewBox="0 0 24 24" focusable="false"><circle cx="12" cy="12" r="10" fill="rgb(var(--successColor))"/><path d="M7.5 12.5l2.5 2.5 6-6" fill="none" stroke="rgb(var(--successBackground))" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

export function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

export function isTouch() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
}

export function motionReduced(event) {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function minWidthQuery(minWidth = 768) {
  return window.matchMedia(`(min-width: ${minWidth}px)`);
}

/**
 * Viewport height that ignores iOS Safari/Chrome URL-bar collapse/expand.
 * Width / orientation changes reset it; small height-only shrinks are ignored.
 */
let _stableViewportHeight = 0;
let _stableViewportWidth = 0;

function _measureViewportHeight() {
  const visualHeight = window.visualViewport?.height || 0;
  return Math.round(Math.max(window.innerHeight, document.documentElement.clientHeight, visualHeight));
}

export function getStableViewportHeight() {
  const width = window.innerWidth;
  const candidate = _measureViewportHeight();
  const widthChanged = Math.abs(width - _stableViewportWidth) > 1;

  if (!_stableViewportHeight || widthChanged) {
    _stableViewportHeight = candidate;
    _stableViewportWidth = width;
    return _stableViewportHeight;
  }

  // URL bar hidden → height grows; adopt the larger value.
  if (candidate > _stableViewportHeight + 2) {
    _stableViewportHeight = candidate;
  }

  // Ignore typical chrome shrink (~50–100px). Only adopt large shrinks (keyboard / real resize).
  if (candidate < _stableViewportHeight - 120) {
    _stableViewportHeight = candidate;
  }

  return _stableViewportHeight;
}

/** Cached dynamic import — first slider pays load cost; later calls reuse the same module. */
let swiperModulePromise = null;

export function loadSwiper() {
  if (!swiperModulePromise) {
    swiperModulePromise = import('@lib/vendor').then((mod) => mod.default);
  }
  return swiperModulePromise;
}

export async function createSlider(element, params = {}) {
  if (!element) return null;

  const { bypassLcpGate, ...swiperParams } = params;

  try {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const isDesignMode = Boolean(window.Shopify?.designMode);

    if (isMobile && !isDesignMode && !bypassLcpGate && typeof window.skyAfterLCP === 'function') {
      await new Promise((resolve) => window.skyAfterLCP(resolve));
    }
  } catch (_) { }

  const Swiper = await loadSwiper();

  await new Promise((resolve) => requestAnimationFrame(resolve));

  const activeClass = element.classList.contains('swiper-visible') ? 'swiper-visible' : null;
  const userOn = swiperParams.on;

  const swiper = new Swiper(element, {
    ...swiperParams,
    observer: false,
    observeParents: false,
    observeSlideChildren: false,
    resizeObserver: false,
    on: {
      ...(userOn || {}),
      init() {
        if (activeClass) element.classList.add(activeClass);
        userOn?.init?.call(this);
      },
      destroy() {
        if (activeClass) element.classList.remove(activeClass);
        userOn?.destroy?.call(this);
      },
    },
  });

  return swiper;
}

/**
 * Creates or reuses an IntersectionObserver with given config.
 * once / callOnExit are stored per target so sections sharing the same
 * IntersectionObserver options can still use different animation modes.
 */
const observerMap = new Map();
export function getObserver(options = {}, once = true, callOnExit = false) {
  const key = JSON.stringify(options);

  if (!observerMap.has(key)) {
    const targets = new WeakMap();

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        const target = targets.get(entry.target);
        if (!target) return;

        const { callback, once: targetOnce, callOnExit: targetCallOnExit } = target;
        const shouldRun = entry.isIntersecting || targetCallOnExit;
        if (callback && shouldRun) {
          callback(entry);

          if (targetOnce && entry.isIntersecting) {
            obs.unobserve(entry.target);
            targets.delete(entry.target);
          }
        }
      });
    }, options);

    observerMap.set(key, {
      observer,
      targets,
    });
  }

  const { observer, targets } = observerMap.get(key);

  return {
    observe(el, callback) {
      targets.set(el, { callback, once, callOnExit });
      observer.observe(el);
    },
    unobserve(el) {
      targets.delete(el);
      observer.unobserve(el);
    },
  };
}

export function lockLenis() {
  window.lenis?.stop();
}

export function unlockLenis() {
  window.lenis?.start();
}

let scrollLockY = 0;
let scrollLocked = false;

function needsIOSScrollLock() {
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

export function lockScroll() {
  if (!scrollLocked) {
    document.body.classList.add('overflow-hidden');

    // if (needsIOSScrollLock()) {
    //   scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    //   document.documentElement.classList.add('scroll-locked');
    //   document.body.style.position = 'fixed';
    //   document.body.style.top = `-${scrollLockY}px`;
    //   document.body.style.left = '0';
    //   document.body.style.right = '0';
    //   document.body.style.width = '100%';
    // }

    scrollLocked = true;
  }
  lockLenis();
}

export function unlockScroll() {
  if (scrollLocked) {
    document.body.classList.remove('overflow-hidden');

    // if (document.documentElement.classList.contains('scroll-locked')) {
    //   document.documentElement.classList.remove('scroll-locked');
    //   document.body.style.position = '';
    //   document.body.style.top = '';
    //   document.body.style.left = '';
    //   document.body.style.right = '';
    //   document.body.style.width = '';
    //   window.scrollTo(0, scrollLockY);
    // }

    scrollLocked = false;
  }
  unlockLenis();
}

const A11Y_FOCUSABLE_SELECTOR =
  'a[href], area[href], button, input:not([type="hidden"]), select, textarea, summary, iframe, object, [tabindex], [contenteditable="true"]';

/**
 * Accessibility stand-in for the inert attribute:
 * - aria-hidden for assistive tech
 * - data-a11y-disabled + CSS pointer-events for pointer blocking
 * - tabindex=-1 on focusable descendants so Tab cannot enter closed surfaces
 */
export function setInteractionDisabled(element, disabled) {
  if (!element) return;

  if (disabled) {
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('data-a11y-disabled', 'true');

    if (element.contains(document.activeElement)) {
      try {
        document.activeElement.blur();
      } catch (_) { }
    }

    element.querySelectorAll(A11Y_FOCUSABLE_SELECTOR).forEach((el) => {
      if (!el.hasAttribute('data-a11y-tabindex')) {
        el.setAttribute('data-a11y-tabindex', el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '');
      }
      el.setAttribute('tabindex', '-1');
    });
    return;
  }

  element.removeAttribute('aria-hidden');
  element.removeAttribute('data-a11y-disabled');

  element.querySelectorAll('[data-a11y-tabindex]').forEach((el) => {
    // Keep nested disabled surfaces (e.g. cart recommended sidebar) untouched
    const disabledRoot = el.closest('[data-a11y-disabled]');
    if (disabledRoot && disabledRoot !== element) return;

    const prev = el.getAttribute('data-a11y-tabindex');
    if (prev === '') {
      el.removeAttribute('tabindex');
    } else {
      el.setAttribute('tabindex', prev);
    }
    el.removeAttribute('data-a11y-tabindex');
  });
}

export function setDrawerClosedAccessibility(drawer) {
  setInteractionDisabled(drawer, true);
}

export function setDrawerOpenAccessibility(drawer) {
  setInteractionDisabled(drawer, false);
}

function initA11yDisabledSurfaces() {
  document.querySelectorAll('[data-a11y-disabled]').forEach((el) => {
    setInteractionDisabled(el, true);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initA11yDisabledSurfaces, { once: true });
} else {
  initA11yDisabledSurfaces();
}

export function closeDrawer(element) {
  const drawer = element.closest('[data-drawer]');
  if (!drawer) return;

  drawer.removeAttribute('active');
  clearTimeout(drawer._closeOpenTimer);
  drawer._closeOpenTimer = setTimeout(() => {
    if (!drawer.hasAttribute('active')) {
      drawer.removeAttribute('open');
    }
  }, 300);
  unlockScroll();
  setDrawerClosedAccessibility(drawer);

  drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
}

export function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

export function waitForEvent(element, eventName) {
  return new Promise((resolve) => {
    const done = (event) => {
      if (event.target === element) {
        element.removeEventListener(eventName, done);
        resolve(event);
      }
    };
    element.addEventListener(eventName, done);
  });
}

export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

export function Currency(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  // use global format if not passed
  const formatString = format || window.themeCurrency.moneyFormat;

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (isNaN(number) || number == null) return 0;

    number = (number / 100).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  let value = '';

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

export function emailValidationError(email) {
  const trimmedEmail = email.trim();
  // Check if empty
  if (!trimmedEmail) {
    return window.newsletterStrings?.errorEmpty;
  }
  // Check if contains @ symbol
  if (!trimmedEmail.includes('@')) {
    return window.newsletterStrings?.errorNoAt;
  }
  // Split by @ to check parts
  const parts = trimmedEmail.split('@');
  // Check local part (before @)
  if (!parts[0] || parts[0].length === 0) {
    return window.newsletterStrings?.errorLocalPart;
  }
  // Check if local part has invalid characters (spaces)
  if (/\s/.test(parts[0])) {
    return window.newsletterStrings?.errorSpaces;
  }
  // Check domain part (after @)
  if (!parts[1] || parts[1].length === 0) {
    return window.newsletterStrings?.errorDomain;
  }
  // Check if domain contains a dot
  if (!parts[1].includes('.')) {
    return window.newsletterStrings?.errorNoDot;
  }
  // Check TLD (after last dot)
  const domainParts = parts[1].split('.');
  const tld = domainParts[domainParts.length - 1];
  if (!tld || tld.length < 2) {
    return window.newsletterStrings?.errorTld || window.newsletterStrings?.errorTldInvalid;
  }
  // Check if TLD has invalid characters
  if (!/^[\p{L}0-9-]+$/u.test(tld)) {
    return window.newsletterStrings?.errorTldInvalid;
  }
  // If all checks pass but regex fails, return generic error
  return window.newsletterStrings?.warning;
}

class SectionViewHandler {
  constructor() {
    this.lastScrollY = window.scrollY;
    this.scrollDirection = '';
    this.sections = document.querySelectorAll('.shopify-section');
    this._initScrollListener();
    this._initSectionObserver();
  }

  _initScrollListener() {
    window.addEventListener(
      'scroll',
      () => {
        const currentScrollY = window.scrollY;
        this.scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';
        this.lastScrollY = currentScrollY;
      },
      { passive: true },
    );
  }

  _initSectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        const viewportHeight = window.innerHeight;

        entries.forEach((entry) => {
          const section = entry.target;
          const rect = entry.boundingClientRect;

          const isVisible = rect.top < viewportHeight && rect.bottom > 0;
          section.classList.toggle('section-in-viewport', isVisible);
        });
      },
      {
        threshold: [0],
      },
    );

    this.sections.forEach((section) => observer.observe(section));
  }
}

// `section-in-viewport` is currently not consumed by any CSS/JS in this repo.
// Avoid doing a full-page IntersectionObserver on every section (one of your Force Reflow sources).
// Keep it only for designMode so any future debug styling doesn't regress editor workflows.
if (window.Shopify?.designMode) {
  new SectionViewHandler();
}

class LazyMarquee extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.initialized = false;
    this.responsive = this.dataset.marqueeMobile === 'false';
    this.mediaQuery = minWidthQuery(768);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // If it's mobile and we should disable on mobile, bail early
    if (this.responsive && !this.mediaQuery.matches) return;

    if ('IntersectionObserver' in window) {
      /*
      this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
        rootMargin: '150px 0px',
        threshold: 0,
      });
      this.observer.observe(this);
      */
      getObserver({ rootMargin: '150px 0px', threshold: 0 }, true, false).observe(this, () => {
        this.initMarquee();
      });
    } else {
      this.initMarquee(); // Fallback
    }

    if (this.responsive) {
      this.mediaQuery.addEventListener('change', this.handleResize);
    }
  }

  disconnectedCallback() {
    if (this.responsive) {
      this.mediaQuery.removeEventListener('change', this.handleResize);
    }
  }

  handleResize(e) {
    if (e.matches) {
      // We entered desktop size
      if (!this.initialized) {
        this.initMarquee();
      }
    } else {
      // We entered mobile size
      if (this.initialized) {
        this.destroyMarquee();
      }
    }
  }

  onIntersection(entries) {
    const entry = entries[0];
    if (entry.isIntersecting && !this.initialized) {
      this.initMarquee();
      this.observer.unobserve(this);
    }
  }

  initMarquee() {
    if (this.initialized || !this.firstElementChild) return;

    const child = this.firstElementChild;
    const parentWidth = this.offsetWidth;
    const childWidth = child.offsetWidth;

    // Empty content → childWidth is 0 → Infinity clones and freezes the page
    if (!parentWidth || !childWidth) return;

    const requiredClones = Math.ceil(parentWidth / childWidth) + 2;
    if (!Number.isFinite(requiredClones) || requiredClones < 1) return;

    this.initialized = true;

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < requiredClones; i++) {
      const clone = child.cloneNode(true);
      // setInteractionDisabled(clone, true);// disabled, because it was causing issue in tab focus 
      clone.setAttribute('priority-hint', 'low');
      fragment.appendChild(clone);
    }

    this.appendChild(fragment);
    this.classList.add('marquee-initialized');
  }

  destroyMarquee() {
    if (!this.initialized) return;
    // Remove only clones (skip firstElementChild)
    while (this.children.length > 1) {
      this.removeChild(this.lastElementChild);
    }
    this.classList.remove('marquee-initialized');
    this.initialized = false;
  }
}
customElements.define('lazy-marquee', LazyMarquee);

export class SkyLazyMedia extends HTMLElement {
  static youtubeAPILoaded = false;
  static vimeoAPILoaded = false;
  constructor() {
    super();
    this.id = this.id || crypto.randomUUID();
    this.template = this.querySelector('template');
    this.poster = this.querySelector('.video-poster');
    this.playbutton = this.querySelector('[data-action="play"]');
    this.content = this.querySelector('[data-video-banner-content]');
    this.media = null;
    this.loaded = false;
    this.shouldAutoplay = this.dataset.autoplay === 'true';
    this.slideAware = this.dataset.slideAware === 'true';
    // this.contentVisibility = this.dataset.contentVisibility; // expects 'always' or 'hide_on_play'

    const clickTarget = this.playbutton;
    clickTarget?.addEventListener('click', () => {
      this._playTrigger = 'manual';
      this.loadAndPlayMedia();

      clickTarget.style.transition = 'opacity 0.5s ease';
      clickTarget.style.opacity = '0';
      setTimeout(() => {
        clickTarget.classList.add('hidden');
      }, 500); // Match the duration of the fade
    });

    this.initObserver();
  }

  disconnectedCallback() {
    // SkyLazyMedia.instances.delete(this);
  }

  static pauseAll() {
    document.querySelectorAll('sky-lazy-media').forEach((el) => {
      el.pauseMedia?.();
    });
    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI?.pause) {
        model.modelViewerUI.pause();
      }
    });
  }

  /**
   * Pause other sky-lazy-media (and product-model) based on context.
   * - Product (inside media-gallery): always one at a time → pause all others.
   * - General: manual play (button click) → pause all others; autoplay (viewport) → don't pause (multiple can play).
   * Optional hook: SkyLazyMedia.beforePauseOthers(playingEl, others) may return array to pause, or undefined to use default.
   */
  static pauseAllExcept(target) {
    const others = Array.from(document.querySelectorAll('sky-lazy-media')).filter((el) => el !== target);

    let toPause;
    if (typeof SkyLazyMedia.beforePauseOthers === 'function') {
      toPause = SkyLazyMedia.beforePauseOthers(target, others);
    }
    if (!Array.isArray(toPause)) {
      // Default: product = always pause others; general = manual pause others, autoplay don't pause
      const inProductGallery = target.closest?.('media-gallery');
      const trigger = target._playTrigger;
      if (inProductGallery) {
        toPause = others;
      } else if (trigger === 'manual') {
        toPause = others;
      } else {
        toPause = [];
      }
    }

    toPause.forEach((el) => el.pauseMedia?.());

    if (toPause.length > 0) {
      document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI?.pause) {
          model.modelViewerUI.pause();
        }
      });
    }
  }

  initObserver() {
    getObserver({ threshold: 0.3 }, false, true).observe(this, (entry) => {
      const ratio = entry.intersectionRatio;
      if (ratio > 0.3) {
        if (!this.shouldAutoplay) return;

        // Hidden product galleries (mobile on desktop / desktop on mobile) must
        // not autoplay — offsetParent is null when display:none.
        if (this.offsetParent === null) return;

        // Slide-aware videos wait for the active Swiper slide. When there is no
        // .swiper-slide parent (single-media gallery falls back to grid), treat
        // as normal viewport autoplay.
        const parentSlide = this.slideAware ? this.closest('.swiper-slide') : null;
        const canAutoplay =
          !this.slideAware ||
          !parentSlide ||
          parentSlide.classList.contains('swiper-slide-active');

        if (!canAutoplay) return;
        this._playTrigger = 'autoplay';
        if (!this.slideAware || !parentSlide) {
          SkyLazyMedia.pauseAllExcept(this);
        }
        this.loadAndPlayMedia();
      } else {
        this.pauseMedia();
      }
    });

  }

  loadContent() {
    if (this.loaded) return;

    const fragment = this.template.content.cloneNode(true);
    const mediaElement = fragment.querySelector('video, iframe, model-viewer');

    if (!mediaElement) return;
    this.media = mediaElement;
    this.template.insertAdjacentElement('afterend', this.media);

    this.loaded = true;
    this.setAttribute('loaded', true);

    // Safari iframe fix
    const previousStyle = this.media.getAttribute('style');
    this.media.setAttribute('style', 'display: block;');
    setTimeout(() => {
      if (previousStyle !== null) {
        this.media.setAttribute('style', previousStyle);
      } else {
        this.media.removeAttribute('style');
      }
    }, 0);

    // Set as ready when iframe loads
    if (this.media.tagName === 'IFRAME') {
      this.media.addEventListener('load', () => {
        this.media.setAttribute('data-ready', 'true');
      });
    }

    // Add YouTube player listener
    if (this.isYouTube()) {
      SkyLazyMedia.loadYouTubeAPI().then(() => {
        this.youTubePlayer = new YT.Player(this.media, {
          events: {
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                SkyLazyMedia.pauseAllExcept(this);
              }
            },
          },
        });
      });
    }

    // Add Vimeo player listener
    if (this.isVimeo()) {
      SkyLazyMedia.loadVimeoAPI().then(() => {
        this.vimeoPlayer = new Vimeo.Player(this.media);
        this.vimeoPlayer.on('play', () => {
          SkyLazyMedia.pauseAllExcept(this);
        });
      });
    }

    // HTML5 and model-viewer play detection
    if (this.media.tagName === 'VIDEO' || this.media.tagName === 'MODEL-VIEWER') {
      this.media.addEventListener('play', () => {
        SkyLazyMedia.pauseAllExcept(this);
      });
    }
  }

  loadAndPlayMedia() {
    const firstLoad = !this.loaded;
    if (firstLoad) this.loadContent();

    this.playMedia();

    // if (this.poster) {
    //   this.poster.classList.add('hidden');
    // }
    if (this.poster) {
      this.poster.style.transition = 'opacity 0.5s ease';
      this.poster.style.opacity = '0';
      setTimeout(() => {
        this.poster.classList.add('hidden');
      }, 500); // Match the duration of the fade
    }
    /* Removed this setting, [content always and hide on play]
    if (firstLoad && this.content && this.contentVisibility === 'hide_on_play') {
      this.content.classList.add('hidden');
    }
    */
  }

  playMedia() {
    if (!this.media) return;

    if (this.media.tagName === 'VIDEO') {
      const playPromise = this.media.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.warn('Autoplay failed:', err);
        });
      }
    } else if (this.isYouTube()) {
      this.media.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (this.isVimeo()) {
      this.media.contentWindow.postMessage({ method: 'play' }, '*');
    }
    this.setAttribute('data-status', 'playing');
  }

  pauseMedia() {
    // Use loaded media if available
    if (this.media) {
      if (this.media.tagName === 'VIDEO') {
        this.media.pause();
      } else if (this.isYouTube() || this.isVimeo()) {
        if (this.media.getAttribute('data-ready') === 'true') {
          const message = this.isYouTube() ? '{"event":"command","func":"pauseVideo","args":""}' : { method: 'pause' };
          this.media.contentWindow.postMessage(message, '*');
        }
      } else if (this.media.tagName === 'MODEL-VIEWER' && typeof this.media.pause === 'function') {
        this.media.pause();
      }
      this.setAttribute('data-status', 'paused');
      return;
    }

    // Fallback: try to find any media inside the element and pause it
    const fallbackMedia = this.querySelector('video, iframe, model-viewer, product-model');

    if (fallbackMedia) {
      if (fallbackMedia.tagName === 'VIDEO') {
        fallbackMedia.pause();
      } else if (fallbackMedia.src?.includes?.('youtube.com')) {
        fallbackMedia.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (fallbackMedia.src?.includes?.('vimeo.com')) {
        fallbackMedia.contentWindow.postMessage({ method: 'pause' }, '*');
      } else if (fallbackMedia.tagName === 'MODEL-VIEWER') {
        fallbackMedia.pause?.();
      } else if (fallbackMedia.tagName === 'PRODUCT-MODEL') {
        const modelViewer = fallbackMedia.querySelector('model-viewer');
        modelViewer?.pause?.();
      }
    }
  }

  isYouTube() {
    return this.media?.src?.includes('youtube.com');
  }

  isVimeo() {
    return this.media?.src?.includes('vimeo.com');
  }

  static loadYouTubeAPI() {
    return new Promise((resolve) => {
      if (SkyLazyMedia.youtubeAPILoaded) return resolve();
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      window.onYouTubeIframeAPIReady = () => {
        SkyLazyMedia.youtubeAPILoaded = true;
        resolve();
      };
      document.head.appendChild(tag);
    });
  }

  static loadVimeoAPI() {
    return new Promise((resolve) => {
      if (SkyLazyMedia.vimeoAPILoaded) return resolve();
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.onload = () => {
        SkyLazyMedia.vimeoAPILoaded = true;
        resolve();
      };
      document.head.appendChild(script);
    });
  }
}
customElements.define('sky-lazy-media', SkyLazyMedia);

class SkyVariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event.target);

      dispatch(SKY_THEME_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata(target) {
    if (!target) return;

    if (target.tagName === 'SELECT') {
      this.syncSelectSelectedAttributes(target);
      this.updateDropdownSelectedValue(target);
      return;
    }

    if (target.tagName === 'INPUT' && target.type === 'radio') {
      const selectedValueEl = target.closest('fieldset')?.querySelector('[data-selected-value]');
      if (selectedValueEl) selectedValueEl.textContent = target.value;
    }
  }

  syncSelectSelectedAttributes(select) {
    Array.from(select.options).forEach((option) => option.removeAttribute('selected'));

    if (select.selectedOptions[0]) {
      select.selectedOptions[0].setAttribute('selected', 'selected');
    }
  }

  updateDropdownSelectedValue(select) {
    const selectedOption = select.selectedOptions[0];
    if (!selectedOption) return;

    const selectedValueEl = select
      .closest('.product-form-input, .product-form__input')
      ?.querySelector('[data-selected-value]');

    if (selectedValueEl) {
      selectedValueEl.textContent = selectedOption.textContent.trim();
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    const values = [];

    this.querySelectorAll('select').forEach((select) => {
      const optionValueId = select.selectedOptions[0]?.dataset.optionValueId;
      if (optionValueId) values.push(optionValueId);
    });

    this.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
      if (input.dataset.optionValueId) values.push(input.dataset.optionValueId);
    });

    return values;
  }
}
customElements.define('sky-variant-picker', SkyVariantPicker);

class ProductRecommendations extends HTMLElement {
  _observer = null;
  _sliderInstance = null;

  connectedCallback() {
    if (this.hasRecommendations()) {
      this.dataset.loaded = 'true';
      this.initSlider();
      return;
    }

    this.initObserver();
  }

  disconnectedCallback() {
    this.destroySlider();
    this._observer?.unobserve(this);
    this._observer = null;
  }

  hasRecommendations() {
    return Boolean(this.querySelector('[data-recommendations-content]'));
  }

  initObserver() {
    this._observer?.unobserve(this);
    this._observer = getObserver({ rootMargin: '0px 0px 400px 0px' }, true, false);
    this._observer.observe(this, () => this.loadRecommendations());
  }

  initSlider() {
    this.destroySlider();

    const sliderEl = this.querySelector('[data-params]');
    if (!sliderEl?.dataset.params) return;

    createSlider(sliderEl, JSON.parse(sliderEl.dataset.params)).then((instance) => {
      this._sliderInstance = instance;
    });
  }

  destroySlider() {
    if (this._sliderInstance?.destroy) {
      this._sliderInstance.destroy(true, true);
    }
    this._sliderInstance = null;
  }

  loadRecommendations() {
    const { url, sectionId, productId } = this.dataset;
    if (!url || !sectionId || !productId) return;

    fetch(`${url}&product_id=${productId}&section_id=${sectionId}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendations fetch failed: ${response.status}`);
        return response.text();
      })
      .then((text) => {
        this.destroySlider();

        const doc = document.createElement('div');
        doc.innerHTML = text;
        const source = doc.querySelector('product-recommendations');

        if (source?.innerHTML.trim()) {
          this.innerHTML = source.innerHTML;
        }

        if (!this.hasRecommendations()) {
          if (this.dataset.recommendationsType === 'complementary') {
            this.remove();
          }
          return;
        }

        this.dataset.loaded = 'true';
        this.initSlider();
        this.dispatchEvent(new CustomEvent('product-recommendations:loaded', { bubbles: true }));
      })
      .catch((error) => {
        console.error(error);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.shouldOpenOnMobile = this.hasAttribute('data-mobile-accordion-open');
    this.defaultOpenOnMobile = this.dataset.mobileAccordionOpen === 'true';

    this.mediaQuery = minWidthQuery(768);
    this.isMobile = !this.mediaQuery.matches;

    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onSummaryKeydown = this.onSummaryKeydown.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.summaryElement.addEventListener('click', this.onSummaryClick);
    this.summaryElement.addEventListener('keydown', this.onSummaryKeydown);
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }

    if (this.shouldOpenOnMobile) {
      this.applyMobileDefaultState();
    }

    this.updateFooterSummaryTabIndex();
  }

  connectedCallback() {
    this.summaryElement = this.summaryElement || this.querySelector('summary');
    this.contentElement = this.contentElement || this.querySelector('summary + *');
    this.updateFooterSummaryTabIndex();
  }

  disconnectedCallback() {
    this.summaryElement.removeEventListener('click', this.onSummaryClick);
    this.summaryElement.removeEventListener('keydown', this.onSummaryKeydown);
    this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    const attributeMismatch = value !== this.hasAttribute('open');

    if (value !== this._open || attributeMismatch) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();
  }

  get isMobileOpenCardView() {
    return (
      this.isMobile &&
      this.defaultOpenOnMobile &&
      this.dataset.section === 'collapsible-tabs'
    );
  }

  get isFooterAccordion() {
    return this.classList.contains('footer-accordion-details') || this.dataset.section === 'footer';
  }

  updateFooterSummaryTabIndex() {
    if (!this.isFooterAccordion || !this.summaryElement) return;

    if (this.isMobile) {
      this.summaryElement.removeAttribute('tabindex');
      this.summaryElement.removeAttribute('aria-disabled');
    } else {
      this.summaryElement.setAttribute('tabindex', '-1');
      this.summaryElement.setAttribute('aria-disabled', 'true');
    }
  }

  onSummaryClick(event) {
    event.preventDefault();

    if (this.isMobileOpenCardView) return;
    if (!this.isMobile && this.isFooterAccordion) return;

    this.open = !this.open;
  }

  onSummaryKeydown(event) {
    const shouldIgnoreToggleKeys =
      this.isMobileOpenCardView || (!this.isMobile && this.isFooterAccordion);

    if (!shouldIgnoreToggleKeys) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
    }
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  transition(value) {
    this.getAnimations().forEach((animation) => animation.cancel());
    this.contentElement?.getAnimations().forEach((animation) => animation.cancel());

    this.style.overflow = 'hidden';

    // Update dimensions dynamically
    const summaryHeight = this.summaryElement.clientHeight;
    const contentHeight = this.contentElement.scrollHeight;

    if (value) {
      this.setAttribute('open', '');

      const startHeight = `${summaryHeight}px`;
      const endHeight = `${summaryHeight + contentHeight}px`;

      this.animate({ height: [startHeight, endHeight] }, { duration: 250, easing: 'ease' }).onfinish = () => {
        if (!this._open) return;
        this.style.height = 'auto';
        this.style.overflow = 'visible';
      };

      this.contentElement.style.opacity = 1;
      this.contentElement.style.transform = 'translateY(0)';
    } else {
      const startHeight = `${this.clientHeight}px`;
      const endHeight = `${summaryHeight}px`;

      this.animate({ height: [startHeight, endHeight] }, { duration: 250, easing: 'ease' }).onfinish = () => {
        if (this._open) return;
        this.removeAttribute('open');
        this.style.height = 'auto';
        this.style.overflow = 'visible';
      };

      this.contentElement.animate(
        { opacity: [1, 0], transform: ['translateY(0)', 'translateY(10px)'] },
        { duration: 150, easing: 'ease' },
      ).onfinish = () => {
        if (this._open) return;
        this.contentElement.style.opacity = 0;
      };
    }
  }

  onBreakpointChange() {
    const wasMobile = this.isMobile;
    this.isMobile = !this.mediaQuery.matches;

    this.updateFooterSummaryTabIndex();

    if (!this.shouldOpenOnMobile) return;

    if (this.isMobile === wasMobile) return;

    if (this.isMobile) {
      this.open = this.defaultOpenOnMobile;
    } else if (this.dataset.section === 'collapsible-tabs') {
      this.open = false;
    } else {
      this.open = true;
    }
  }

  applyMobileDefaultState() {
    if (this.isMobile) {
      if (!this.defaultOpenOnMobile) {
        this.open = false;
      } else {
        this.open = true;
      }
    }
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent('toggle', { bubbles: true, detail: { current: this } }),
    );
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class LoadingPage extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('page:loaded', () => {
      this.hide();
    });

    document.addEventListener('loading-page:show', () => {
      this.show();
    });

    document.addEventListener('loading-page:hide', () => {
      this.hide();
    });
  }

  show() {
    this.hidden = false;
    // this.style.opacity = 1;
    motion.animate(this, { opacity: 1 }, { duration: 0.2 });
  }

  hide() {
    /*
    motion.animate(this, { opacity: 0 }, { duration: 0.3 }).finished.then(() => {
      this.hidden = true;
    });
    */
    motion.animate(this, { opacity: 0 }, { duration: 1 }).finished.then(() => {
      this.hidden = true;
    });
    document.dispatchEvent(new CustomEvent('page:loader-finished'));
  }
}
customElements.define('loading-page', LoadingPage);

class GridList extends HTMLElement {
  constructor() {
    super();

    this.motionReduced =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.hasAttribute('motion-reduced');

    // bind editor handler
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
  }

  connectedCallback() {
    if (!this.motionReduced) {
      this.hideGridItems();

      motion.inView(
        this,
        () => {
          this.showGridItems();
        },
        {
          margin: '0px 0px -150px 0px',
        },
      );
    }

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener('shopify:block:select', this.handleSectionLoad);
    }
  }

  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      document.removeEventListener('shopify:block:select', this.handleSectionLoad);
    }
  }

  handleSectionLoad(event) {
    // ensure event belongs to this section
    if (!event.target.contains(this)) return;

    this.reShowVisibleGridItems();
  }

  get gridItems() {
    return this.querySelectorAll('[data-item]');
  }

  get visibleGridItems() {
    return this.querySelectorAll('[data-item]:not([style])');
  }

  hideGridItems() {
    if (!this.gridItems.length) return;

    this.gridItems.forEach((item) => {
      item.style.transform = 'translateY(3.5rem)';
      item.style.opacity = '0.01';
      item.style.visibility = 'hidden';
    });
  }

  showGridItems() {
    if (!this.gridItems.length) return;

    motion.animate(
      this.gridItems,
      {
        transform: ['translateY(3.5rem)', 'translateY(0)'],
        opacity: [0.01, 1],
        visibility: ['hidden', 'visible'],
      },
      {
        duration: 0.5,
        delay: this.motionReduced ? 0 : motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      },
    );
  }

  reShowVisibleGridItems() {
    if (!this.visibleGridItems.length) return;

    motion.animate(
      this.visibleGridItems,
      {
        transform: ['translateY(3.5rem)', 'translateY(0)'],
        opacity: [0.01, 1],
        visibility: ['hidden', 'visible'],
      },
      {
        duration: 0.5,
        delay: this.motionReduced ? 0 : motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      },
    );
  }
}
customElements.define('grid-list', GridList);

class TabComponent extends HTMLElement {
  constructor() {
    super();

    this.tabList = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.activeIndex = this.getInitialIndex();
    this.isAnimating = false;
    this.pendingIndex = null;
    this.motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.translateY = '2rem';
  }

  connectedCallback() {
    this.setupAccessibility();
    this.bindEvents();
    this.initPanels();
  }

  refresh(preferredIndex) {
    this.tabList?.removeEventListener('keydown', this.onKeyDown);
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
    });

    this.tabList = this.querySelector('[role="tablist"]');
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.isAnimating = false;
    this.pendingIndex = null;

    if (!this.tabs.length) {
      this.activeIndex = null;
      return;
    }

    const preferred =
      preferredIndex != null && this.getTab(preferredIndex)
        ? String(preferredIndex)
        : String(this.tabs[0].dataset.index);

    this.activeIndex = preferred;

    this.tabs.forEach((tab) => {
      const isActive = String(tab.dataset.index) === preferred;
      tab.classList.toggle('active', isActive);
    });

    this.querySelectorAll('[data-tab-content]').forEach((panel) => {
      const isActive = String(panel.dataset.tabContent) === preferred;
      panel.classList.toggle('active', isActive);
      panel.hidden = !isActive;
      this.clearPanelStyles(panel);
    });

    this.setupAccessibility();
    this.bindEvents();
  }

  getInitialIndex() {
    const active = this.querySelector('[role="tab"].active');
    if (active) return active.dataset.index;
    return this.tabs[0]?.dataset.index || '1';
  }

  getPanel(index) {
    return this.querySelector(`[data-tab-content="${index}"]`);
  }

  getTab(index) {
    return this.tabs.find((tab) => tab.dataset.index === String(index));
  }

  setupAccessibility() {
    this.tabs.forEach((tab) => {
      const index = tab.dataset.index;
      const panel = this.getPanel(index);

      tab.setAttribute('aria-selected', tab.classList.contains('active'));
      tab.setAttribute('tabindex', tab.classList.contains('active') ? '0' : '0');

      if (panel && panel.id) {
        if (!tab.id) tab.id = `tab-${index}`;
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', tab.id);
        // panel.setAttribute('tabindex', '0');
        panel.removeAttribute('tabindex');
      }
    });
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
    });

    this.tabList?.addEventListener('keydown', this.onKeyDown);
  }

  initPanels() {
    this.tabs.forEach((tab) => {
      const index = tab.dataset.index;
      const panel = this.getPanel(index);
      if (!tab.classList.contains('active') && panel) {
        // panel.hidden = true;
      }
    });
  }

  onTabClick = (e) => {
    e.preventDefault();
    this.requestTab(e.currentTarget.dataset.index);
  };

  onKeyDown = (e) => {
    if (![37, 39].includes(e.keyCode)) return;
    e.preventDefault();

    let current = this.tabs.findIndex((t) => t.dataset.index === this.activeIndex);
    let next = current;

    if (e.keyCode === 39) next++;
    if (e.keyCode === 37) next--;

    if (next >= this.tabs.length) next = 0;
    if (next < 0) next = this.tabs.length - 1;

    const nextTab = this.tabs[next];
    nextTab.focus();
    this.requestTab(nextTab.dataset.index);
  };

  requestTab(index) {
    if (index == null) return;

    // Queue latest target while animating so fast clicks settle on the last choice
    if (this.isAnimating) {
      this.pendingIndex = index;
      this.updateTabState(index);
      this.dispatchTabChangeStart(index);
      return;
    }

    if (String(index) === String(this.activeIndex)) return;
    this.dispatchTabChangeStart(index);
    this.changeTab(index);
  }

  dispatchTabChangeStart(index) {
    this.dispatchEvent(
      new CustomEvent('tabChangeStart', {
        bubbles: true,
        detail: {
          index,
          panel: this.getPanel(index),
          tab: this.getTab(index),
        },
      }),
    );
  }

  updateTabState(index) {
    const toTab = this.getTab(index);
    if (!toTab) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.index === String(index);
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '0');
    });

    if (this.dataset.tabs === 'mobile-category') {
      toTab.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }

    this.syncLinkedElements(index);
    return toTab;
  }

  clearPanelStyles(panel) {
    if (!panel) return;
    panel.style.opacity = '';
    panel.style.transform = '';
    panel.style.pointerEvents = '';
  }

  async changeTab(newIndex) {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const fromIndex = this.activeIndex;
    const toTab = this.updateTabState(newIndex);
    const fromPanel = this.getPanel(fromIndex);
    const toPanel = this.getPanel(newIndex);

    if (!toTab || !toPanel) {
      this.isAnimating = false;
      return;
    }

    if (fromPanel) fromPanel.classList.remove('active');
    toPanel.classList.add('active');

    await this.ensurePanelContent(toPanel);
    await this.transition(fromPanel, toPanel);

    this.activeIndex = newIndex;

    this.dispatchEvent(
      new CustomEvent('tabChange', {
        bubbles: true,
        detail: {
          index: newIndex,
          panel: toPanel,
          tab: toTab,
        },
      }),
    );

    this.isAnimating = false;

    const pending = this.pendingIndex;
    this.pendingIndex = null;
    if (pending != null && String(pending) !== String(this.activeIndex)) {
      await this.changeTab(pending);
    }
  }

  /**
   * Syncs `active` class on any elements inside this component
   * that carry a `data-other-attr-index` attribute.
   *
   * Usage: add  data-other-attr-index="<tabIndex>"  to any element
   * and it will automatically receive / lose the `active` class
   * whenever the matching tab becomes active.
   *
   * Fully generic — works in any section that uses <tab-component>.
   */
  syncLinkedElements(newIndex) {
    this.querySelectorAll('[data-other-attr-index]').forEach((el) => {
      const isMatch = el.dataset.otherAttrIndex === String(newIndex);
      el.classList.toggle('active', isMatch);
    });
  }

  async ensurePanelContent(panel) {
    const template = panel.querySelector('template[data-type="tab-content"]');
    if (!template || panel.dataset.loaded === 'true') return;

    const clone = template.content.cloneNode(true);
    panel.appendChild(clone);
    panel.dataset.loaded = 'true';

    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
  }

  async transition(fromPanel, toPanel) {
    if (!fromPanel || !toPanel) return;

    // Keep original motion: item stagger + panel translate.
    // Only extras vs original: style cleanup + non-stacked sequential (facets height safety).
    this.clearPanelStyles(fromPanel);
    this.clearPanelStyles(toPanel);

    const duration = this.motionReduced ? 0 : 0.3;
    const stacked = Boolean(toPanel.closest('.tabs-panel'));

    if (!stacked) {
      fromPanel.style.pointerEvents = 'none';

      if (duration > 0) {
        await motion.animate(
          fromPanel,
          {
            transform: ['translateY(0)', `translateY(${this.translateY})`],
            opacity: [1, 0],
          },
          { duration },
        ).finished;
      }

      fromPanel.hidden = true;
      this.clearPanelStyles(fromPanel);

      toPanel.style.opacity = '0';
      toPanel.style.transform = `translateY(${this.translateY})`;
      toPanel.hidden = false;

      await new Promise(requestAnimationFrame);

      const grid = toPanel.querySelector('grid-list');
      if (grid && typeof grid.showGridItems === 'function') {
        grid.showGridItems();
      }

      if (duration > 0) {
        await motion.animate(
          toPanel,
          {
            transform: [`translateY(${this.translateY})`, 'translateY(0)'],
            opacity: [0, 1],
          },
          { duration },
        ).finished;
      }

      this.clearPanelStyles(toPanel);
      return;
    }

    // Original stacked-panel flow (shop-by-category, featured products, etc.)
    toPanel.style.opacity = '0';
    toPanel.style.transform = `translateY(${this.translateY})`;
    toPanel.hidden = false;

    await new Promise(requestAnimationFrame);

    // GRID START HERE (important) — one-by-one item stagger, same as before
    const grid = toPanel.querySelector('grid-list');
    if (grid && typeof grid.showGridItems === 'function') {
      grid.showGridItems();
    }

    if (duration > 0) {
      await motion.animate(
        fromPanel,
        {
          transform: ['translateY(0)', `translateY(${this.translateY})`],
          opacity: [1, 0],
        },
        { duration },
      ).finished;
    }

    fromPanel.hidden = true;
    this.clearPanelStyles(fromPanel);

    if (duration > 0) {
      await motion.animate(
        toPanel,
        {
          transform: [`translateY(${this.translateY})`, 'translateY(0)'],
          opacity: [0, 1],
        },
        { duration },
      ).finished;
    }

    this.clearPanelStyles(toPanel);
  }

  getSelected() {
    return {
      index: this.activeIndex,
      panel: this.getPanel(this.activeIndex),
    };
  }
}
customElements.define('tab-component', TabComponent);

if (!customElements.get('copy-button')) {
  customElements.define(
    'copy-button',
    class CopyButton extends HTMLButtonElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      async handleClick(event) {
        event.preventDefault();

        const text = this.dataset.copyText;
        if (!text) return;

        const copied = await copyTextToClipboard(text);
        if (copied) {
          this.classList.add('copied');
          this.setAttribute('aria-label', window.accessibilityStrings.couponCodeCopied);
          setTimeout(() => {
            this.classList.remove('copied');
            this.removeAttribute('aria-label');
          }, 1500);
        } else {
          this.setAttribute('aria-label', window.accessibilityStrings.couponCodeCopyToClipboard);
          setTimeout(() => {
            this.removeAttribute('aria-label');
          }, 2000);
        }
      }
    },
    { extends: 'button' },
  );
}

class MediaOnHover extends HTMLDivElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.enabled = false;
    this.handleMove = this.handleMove.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleLeave = this.handleLeave.bind(this);
    this.handleCapabilityChange = this.handleCapabilityChange.bind(this);
  }

  connectedCallback() {
    this.slider = this.querySelector('.product-card-media-slider');
    this.slides = this.querySelectorAll('.product-card-media-slide');
    this.total = this.slides.length;

    if (!this.slider || this.total < 2) return;

    this.hoverQuery = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
    this.hoverQuery.addEventListener('change', this.handleCapabilityChange);

    if ('IntersectionObserver' in window) {
      this._observer = getObserver({ rootMargin: '200px 0px' }, true, false);
      this._observer.observe(this, () => this.sync());
    } else {
      this.sync();
    }
  }

  disconnectedCallback() {
    this.disable();
    if (this.hoverQuery) {
      this.hoverQuery.removeEventListener('change', this.handleCapabilityChange);
    }
  }

  canEnable() {
    return Boolean(this.hoverQuery?.matches);
  }

  handleCapabilityChange() {
    this.sync();
  }

  sync() {
    if (this.canEnable()) {
      this.enable();
    } else {
      this.disable();
    }
  }

  handleEnter() {
    this.addEventListener('mousemove', this.handleMove);
  }

  handleLeave() {
    this.removeEventListener('mousemove', this.handleMove);
    this.reset();
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.addEventListener('mouseenter', this.handleEnter);
    this.addEventListener('mouseleave', this.handleLeave);
  }

  disable() {
    if (this.enabled) {
      this.enabled = false;
      this.removeEventListener('mouseenter', this.handleEnter);
      this.removeEventListener('mouseleave', this.handleLeave);
      this.removeEventListener('mousemove', this.handleMove);
    }
    this.reset();
  }

  handleMove(e) {
    if (this.raf) return;

    this.raf = requestAnimationFrame(() => {
      this.raf = null;

      const rect = this.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = x / rect.width;

      let index = Math.floor(percent * this.total);
      if (index >= this.total) index = this.total - 1;

      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this.updateSlide();
      }
    });
  }

  updateSlide() {
    const offset = -100 * this.currentIndex;
    this.slider.style.transform = `translate3d(${offset}%, 0, 0)`;
  }

  reset() {
    this.currentIndex = 0;
    if (this.slider) {
      this.slider.style.transform = `translate3d(0, 0, 0)`;
    }
  }
}
customElements.define('media-on-hover', MediaOnHover, { extends: 'div' });

class SplitWords extends HTMLElement {
  constructor() {
    super();
    this._animated = false;
    this._inView = false;
    this._loaderDone = document.body.classList.contains('loaded') || window.Shopify?.designMode;
    this._split = false;
    this._onEditorUpdate = this.handleEditorUpdate.bind(this);
    this.primaryColor = document.body.dataset.headingFlashPrimary;
    this.secondaryColor = document.body.dataset.headingFlashSecondary;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    // split before entering viewport
    if ('IntersectionObserver' in window) {
      getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false).observe(this, () => {
        this.splitWords(this);
        this.tryAnimate();
      });
    } else {
      this.splitWords(this);
    }

    // track inView (but don't animate yet)
    motion.inView(
      this,
      () => {
        this._inView = true;
        this.tryAnimate();
      },
      {
        margin: '0px 0px -15% 0px',
      },
    );

    // listen for preloader finish
    this._onLoaderDone = () => {
      this._loaderDone = true;
      this.tryAnimate();
    };

    this._onPageLoaded = () => {
      if (!document.querySelector('loading-page')) {
        this._onLoaderDone();
      }
    };

    document.addEventListener('page:loader-finished', this._onLoaderDone);
    document.addEventListener('page:loaded', this._onPageLoaded);

    if (document.body.classList.contains('loaded') && !document.querySelector('loading-page')) {
      this._loaderDone = true;
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', this._onEditorUpdate);
      document.addEventListener('shopify:section:reorder', this._onEditorUpdate);
      document.addEventListener('shopify:block:select', this._onEditorUpdate);
      document.addEventListener('shopify:block:deselect', this._onEditorUpdate);
    }

    this.tryAnimate();
  }

  tryAnimate() {
    if (this._animated) return;

    if (this._inView && this._loaderDone && this._split) {
      this._animated = true;
      this.runFlashAnimation();
    }
  }

  splitWords(node) {
    if (this._split) return;
    this._split = true;

    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null, false);

    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((textNode) => {
      if (textNode.parentElement?.closest('[data-word]')) return;

      const text = textNode.nodeValue;
      if (!text.trim()) return;

      const words = text.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      words.forEach((word) => {
        if (word.trim()) {
          const span = document.createElement('span');
          span.classList.add('word', 'split-word');
          span.setAttribute('data-word', 'true');
          span.textContent = word;
          span.style.opacity = 0;
          fragment.appendChild(span);
        } else {
          fragment.appendChild(document.createTextNode(word));
        }
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });
  }

  resetWords() {
    this.querySelectorAll('[data-word]').forEach((word) => {
      word.replaceWith(document.createTextNode(word.textContent));
    });
    this.normalize();
    this._split = false;
    this._animated = false;
  }

  handleEditorUpdate(event) {
    if (!event.target.contains(this)) return;

    requestAnimationFrame(() => {
      this.resetWords();
      this.splitWords(this);
      this.tryAnimate();
    });
  }

  async runFlashAnimation() {
    const words = this.querySelectorAll('[data-word]');

    words.forEach(async (word, i) => {
      // const delay = i * 0.2;
      // const delay = i * 0.09;
      const delay = i * 0.15;
      const finalOpacity = word.dataset.finalOpacity || 1;

      word.style.opacity = 0;

      await motion.animate(word, { opacity: 1, color: this.primaryColor }, { duration: 0.04, delay, easing: 'linear' })
        .finished;

      await motion.animate(word, { color: this.secondaryColor }, { duration: 0.04, easing: 'linear' }).finished;

      await motion.animate(word, { opacity: finalOpacity }, { duration: 0.04, easing: 'linear' }).finished;

      word.style.removeProperty('color');
    });
  }

  disconnectedCallback() {
    document.removeEventListener('page:loader-finished', this._onLoaderDone);
    document.removeEventListener('page:loaded', this._onPageLoaded);
    document.removeEventListener('shopify:section:load', this._onEditorUpdate);
    document.removeEventListener('shopify:section:reorder', this._onEditorUpdate);
    document.removeEventListener('shopify:block:select', this._onEditorUpdate);
    document.removeEventListener('shopify:block:deselect', this._onEditorUpdate);
  }
}
if (!customElements.get('split-words')) {
  customElements.define('split-words', SplitWords);
}

class SplitLines extends HTMLElement {
  constructor() {
    super();
    this._html = null;
    this._width = null;
    this._initialized = false;
    this._splitting = false;
    this._ready = false;
    this._activating = false;
    this._splitRetry = false;
    this._observer = null;
    this._onRecalc = this._onRecalc.bind(this);
    this._onEditorUpdate = this._onEditorUpdate.bind(this);
  }

  connectedCallback() {
    if (this._html === null) this._html = this.innerHTML;
    this.init();
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', this._onEditorUpdate);
      document.addEventListener('shopify:section:reorder', this._onEditorUpdate);
      document.addEventListener('shopify:block:select', this._onEditorUpdate);
      document.addEventListener('shopify:block:deselect', this._onEditorUpdate);
    }

    if ('IntersectionObserver' in window) {
      this._observer = getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false);
      this._observer.observe(this, () => this._onVisible());
    } else {
      this._onVisible();
    }
  }

  disconnectedCallback() {
    this._observer?.unobserve(this);
    this._observer = null;
    layoutRecalcManager.unregister(this._onRecalc);
    document.removeEventListener('shopify:section:load', this._onEditorUpdate);
    document.removeEventListener('shopify:section:reorder', this._onEditorUpdate);
    document.removeEventListener('shopify:block:select', this._onEditorUpdate);
    document.removeEventListener('shopify:block:deselect', this._onEditorUpdate);
  }

  async _onVisible() {
    if (this._ready || this._activating) return;
    this._activating = true;

    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (_) { }
    }

    this._activating = false;
    if (!this.isConnected) return;

    this._ready = true;
    layoutRecalcManager.register(this._onRecalc);
    this._requestSplit();
  }

  _requestSplit() {
    this._width = null;
    if (layoutRecalcManager.isMuted) {
      if (this._splitRetry) return;
      this._splitRetry = true;
      requestAnimationFrame(() => {
        this._splitRetry = false;
        if (!this.isConnected || !this._ready) return;
        this._requestSplit();
      });
      return;
    }
    layoutRecalcManager.request(this._onRecalc);
  }

  _onRecalc(phase) {
    if (phase === 'measure') return;
    if (!this._ready) return;
    this.splitLines();
  }

  _onEditorUpdate(event) {
    if (!event.target.contains(this)) return;
    if (!this.querySelector('[data-line]')) {
      this._html = this.innerHTML;
    }
    if (!this._ready) {
      this._onVisible();
      return;
    }
    this._requestSplit();
  }

  splitLines() {
    if (this._splitting || this._html === null) return;

    const width = Math.round(this.getBoundingClientRect().width);
    if (!width) return;
    if (width === this._width && this.querySelector('[data-line]')) return;

    this._splitting = true;
    layoutRecalcManager.isMuted = true;

    try {
      this.innerHTML = this._html;
      this._wrapWords(this);

      const words = [...this.querySelectorAll('[data-word]')];
      if (!words.length) {
        this._width = width;
        return;
      }

      const lines = [];
      let lineWords = [];
      let lineTop = null;

      words.forEach((word) => {
        // getBoundingClientRect: offsetTop breaks when ancestors are positioned (e.g. a.text-underline)
        const top = word.getBoundingClientRect().top;
        if (lineTop === null || Math.abs(top - lineTop) > 1) {
          if (lineWords.length) lines.push(lineWords);
          lineWords = [word];
          lineTop = top;
        } else {
          lineWords.push(word);
        }
      });
      if (lineWords.length) lines.push(lineWords);

      const fragment = document.createDocumentFragment();

      lines.forEach((lineWordList) => {
        const containerEl = document.createElement('line-element');
        containerEl.className = 'line-container';
        containerEl.setAttribute('data-line-container', '');

        const lineEl = document.createElement('div');
        lineEl.className = 'line';
        lineEl.setAttribute('data-line', '');

        const runs = [];
        lineWordList.forEach((word) => {
          const signature = this._getAncestorSignature(word);
          const last = runs[runs.length - 1];
          if (last && last.signature === signature) {
            last.words.push(word);
          } else {
            runs.push({ signature, words: [word], sample: word });
          }
        });

        runs.forEach((run, index) => {
          if (index > 0) lineEl.appendChild(document.createTextNode(' '));
          const text = run.words.map((word) => word.textContent).join(' ');
          lineEl.appendChild(this._cloneWithAncestors(run.sample, text));
        });

        containerEl.appendChild(lineEl);
        fragment.appendChild(containerEl);
      });

      this.replaceChildren(fragment);
      this._width = width;
    } finally {
      layoutRecalcManager.isMuted = false;
      this._splitting = false;
    }
  }

  _wrapWords(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((textNode) => {
      if (textNode.parentElement?.closest('[data-word]')) return;

      const text = textNode.nodeValue;
      if (!text.trim()) return;

      const parts = text.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      parts.forEach((part) => {
        if (part.trim()) {
          const span = document.createElement('span');
          span.className = 'word';
          span.setAttribute('data-word', '');
          span.style.display = 'inline-block';
          span.textContent = part;
          fragment.appendChild(span);
        } else {
          fragment.appendChild(document.createTextNode(part));
        }
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });
  }

  _getAncestorSignature(word) {
    const parts = [];
    let parent = word.parentElement;

    while (parent && parent !== this) {
      if (parent.hasAttribute('data-word')) {
        parent = parent.parentElement;
        continue;
      }

      parts.push(
        [
          parent.tagName,
          parent.className || '',
          parent.getAttribute('href') || '',
          parent.getAttribute('style') || '',
        ].join('\0'),
      );
      parent = parent.parentElement;
    }

    return parts.join('|');
  }

  _cloneWithAncestors(word, text = word.textContent) {
    let node = document.createTextNode(text);
    let parent = word.parentElement;

    while (parent && parent !== this) {
      if (parent.hasAttribute('data-word')) {
        parent = parent.parentElement;
        continue;
      }

      const clone = parent.cloneNode(false);
      clone.appendChild(node);
      node = clone;
      parent = parent.parentElement;
    }

    return node;
  }
}
if (!customElements.get('split-lines')) {
  customElements.define('split-lines', SplitLines);
}

class LineElement extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._cancelScroll = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;

    const line = this.querySelector('[data-line]');
    if (!line) return;

    if (motionReduced()) {
      line.style.transform = 'translateY(0%)';
      return;
    }

    // Enter: line top at ~85–90% viewport (bottom 10–15%) → y: 100%
    // Done:  line top at ~50–60% viewport → y: 0%
    this._cancelScroll = motion.scroll(
      motion.animate(line, { y: ['100%', '0%'] }, { ease: 'linear' }),
      {
        target: this,
        offset: ['start 90%', 'start 55%'],
      },
    );
  }

  disconnectedCallback() {
    this._cancelScroll?.();
    this._cancelScroll = null;
    this._initialized = false;
  }
}
if (!customElements.get('line-element')) {
  customElements.define('line-element', LineElement);
}

class QuickView extends HTMLButtonElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  async handleClick(event) {
    event.preventDefault();

    const drawer = document.querySelector('[data-drawer="quick-view-drawer"]');
    const productUrl = this.dataset.productUrl;
    if (!drawer) {
      console.error('Quick view drawer not found');
      return;
    }
    if (!productUrl) {
      console.error('Product URL not found on quick view button');
      return;
    }

    const sectionId = drawer.dataset.id;
    const sectionUrl = `${productUrl.split('?')[0]}?section_id=${sectionId}`;

    try {
      const response = await fetch(sectionUrl);
      if (!response.ok) {
        console.error(`Failed to fetch quick view section: ${response.status}`);
        return;
      }
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const [newBody, newFooter] = [
        html.querySelector('[data-body="true"]'),
        html.querySelector('[data-footer="true"]'),
      ];
      const [drawerBody, drawerFooter] = [
        drawer.querySelector('[data-body="true"]'),
        drawer.querySelector('[data-footer="true"]'),
      ];

      if (!newBody || !drawerBody) {
        console.error('Quick view body not found in fetched content or drawer');
        return;
      }

      // Reinject scripts so modules like product-model.js execute after fetch
      HTMLUpdateUtility.setInnerHTML(drawerBody, newBody.innerHTML);
      if (drawerFooter && newFooter) {
        HTMLUpdateUtility.setInnerHTML(drawerFooter, newFooter.innerHTML);
      }

      // product-model.js is often already cached (module won't re-run) — explicitly
      // register ProductJSON + setupXRElements so XR buttons lose data-shopify-xr-hidden.
      this.initQuickViewXR(drawerBody);

      drawer._quickViewTrigger = this;
      if (typeof drawer.open === 'function') {
        drawer.open(this);
      } else {
        setDrawerOpenAccessibility(drawer);
        drawer.setAttribute('open', '');
        drawer.setAttribute('active', '');
        if (typeof lockScroll === 'function') lockScroll();
        const focusTarget = drawer.querySelector('.drawer-close-btn') || drawer;
        trapFocus(drawer, focusTarget);
      }
    } catch (err) {
      console.error('Failed to open quick view:', err);
    }
  }

  initQuickViewXR(container) {
    if (!container?.querySelector?.('[id^="ProductJSON-"], [data-shopify-xr]')) return;

    const run = () => window.ProductModel?.loadShopifyXR();
    run();
    if (window.ProductModel) return;

    // First open on a non-product page: wait for reinjected product-model.js module
    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      if (window.ProductModel) {
        clearInterval(poll);
        window.ProductModel.loadShopifyXR();
      } else if (attempts > 40) {
        clearInterval(poll);
      }
    }, 50);
  }
}
customElements.define('quick-view', QuickView, { extends: 'button' });

class QuickViewDrawer extends HTMLElement {
  constructor() {
    super();
    this._onSectionSelect = this._onSectionSelect.bind(this);
    this._onSectionDeselect = this._onSectionDeselect.bind(this);
    this._onEscapeKeydown = this._onEscapeKeydown.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
    this._onDrawerClose = this._onDrawerClose.bind(this);
    this._quickViewTrigger = null;
    this._focusTimer = null;
    this._qvFocusHandedOff = false;
    this._qvClosing = false;
  }

  connectedCallback() {
    if (Shopify && Shopify.designMode) {
      this.init();
    }
    this.querySelectorAll('[is="close-drawer"]').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
    document.addEventListener('keydown', this._onEscapeKeydown);
    this.addEventListener('drawer:close', this._onDrawerClose);
  }

  get closeButton() {
    return this.querySelector('.drawer-close-btn');
  }

  clearPendingFocus() {
    clearTimeout(this._focusTimer);
    this._focusTimer = null;
    this.removeEventListener('transitionend', this._onTransitionEnd);
  }

  _onTransitionEnd(event) {
    if (event.target !== this && !event.target?.classList?.contains('drawer-inner')) return;
    this.clearPendingFocus();
    this.trapDrawerFocus();
  }

  _onEscapeKeydown(event) {
    if (event.code !== 'Escape') return;
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;
    // Nested overlay (pickup, size chart, etc.) owns Escape until it closes
    if (currentTrapContainer && currentTrapContainer !== this) return;
    event.preventDefault();
    this.close();
  }

  /**
   * Runs for every close path (X, overlay, Escape, closeDrawer from buy-box / ATC).
   * Idempotent: buy-box Escape can fire a second closeDrawer on keyup — must not
   * pop another trap or fight focus restore.
   */
  _onDrawerClose() {
    if (this._qvClosing) return;
    this._qvClosing = true;

    const trigger = this._quickViewTrigger;
    const suppressReturn = this._suppressReturnFocus === true;
    const alreadyHandedOff = this._qvFocusHandedOff === true;

    this._suppressReturnFocus = false;
    this._quickViewTrigger = null;
    this._qvFocusHandedOff = false;
    this.clearPendingFocus();

    if (trigger && typeof trigger.setAttribute === 'function') {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (!alreadyHandedOff) {
      // Direct closeDrawer path (ATC / buy-box Escape) — detach then restore
      if (currentTrapContainer === this) {
        removeTrapFocus(null, { restoreFocus: false });
      }
      if (!suppressReturn) {
        this._restoreOpenerFocus(trigger);
      }
    }

    queueMicrotask(() => {
      this._qvClosing = false;
    });
  }

  /**
   * Reliably return focus to the QV / choose-options opener (hover-hidden card buttons).
   * Must succeed synchronously before closeDrawer blur(), or Chrome parks on the URL bar.
   * @returns {boolean}
   */
  _restoreOpenerFocus(trigger) {
    if (!trigger || typeof trigger.focus !== 'function') return false;
    if (!document.contains(trigger)) return false;
    return restoreFocusTo(trigger);
  }

  /**
   * Guarantee focus is outside the drawer before a11y-disable blur runs.
   * If opener restore failed, land on a safe in-page control — never body.
   */
  _ensureFocusOutsideDrawer(trigger) {
    if (!this.contains(document.activeElement)) return;

    if (this._restoreOpenerFocus(trigger)) return;
    if (!this.contains(document.activeElement)) return;

    const card = trigger?.closest?.('.product-card, .product-card-inline');
    const fallback =
      card?.querySelector(
        'a.product-card-heading, a.full-unstyled-link, .product-card-title a, a[href]:not([tabindex="-1"])',
      ) ||
      document.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');

    if (fallback && !this.contains(fallback)) {
      safeFocus(fallback);
    }
  }

  trapDrawerFocus() {
    if (!this.hasAttribute('active')) return;
    const focusTarget = this.closeButton || this;
    trapFocus(this, focusTarget);
  }

  open(triggeredBy) {
    this._qvClosing = false;
    this._qvFocusHandedOff = false;

    if (triggeredBy) {
      this._quickViewTrigger = triggeredBy;
      if (typeof triggeredBy.setAttribute === 'function') {
        triggeredBy.setAttribute('aria-expanded', 'true');
      }
    }

    setDrawerOpenAccessibility(this);
    this.setAttribute('open', '');
    this.setAttribute('active', '');
    if (typeof lockScroll === 'function') lockScroll();

    this.clearPendingFocus();
    // Move focus to the close (X) button as soon as the drawer is accessible
    requestAnimationFrame(() => {
      this.trapDrawerFocus();
    });
    // Fallback after drawer slide animation in case first focus was interrupted
    this.addEventListener('transitionend', this._onTransitionEnd);
    this._focusTimer = setTimeout(() => {
      this.clearPendingFocus();
      this.trapDrawerFocus();
    }, 400);
  }

  close() {
    if (!this.hasAttribute('open') && !this.hasAttribute('active')) return;
    this.clearPendingFocus();

    const trigger = this._quickViewTrigger;
    const suppressReturn = this._suppressReturnFocus === true;
    // Only pop when QV owns the top trap — never pop a nested overlay (pickup / size chart)
    const ownsTrap = currentTrapContainer === this;

    if (ownsTrap) {
      // Detach FIRST — restoring while trap focusin is active steals focus back into the drawer
      removeTrapFocus(null, { restoreFocus: false });
    }

    if (!suppressReturn) {
      this._restoreOpenerFocus(trigger);
      // Critical: closeDrawer → blur() if focus still inside → Chrome URL bar
      this._ensureFocusOutsideDrawer(trigger);
    }

    // Mark handoff so _onDrawerClose does not restore again after blur
    this._qvFocusHandedOff = true;

    closeDrawer(this);
  }

  init() {
    document.addEventListener('shopify:section:select', this._onSectionSelect);
    document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
  }

  _onSectionSelect(event) {
    if (event.target.contains(this)) {
      this.open();
    }
  }

  _onSectionDeselect(event) {
    if (event.target.contains(this)) {
      this.close();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:select', this._onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
    document.removeEventListener('keydown', this._onEscapeKeydown);
    this.removeEventListener('drawer:close', this._onDrawerClose);
    this.clearPendingFocus();
    unlockScroll();
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

if (!customElements.get('recently-viewed')) {
  class RecentlyViewed extends HTMLElement {
    static MAX_ITEMS = 5;

    constructor() {
      super();
      this._observer = null;
      this._loaded = false;
      this.loadRecentlyViewed = this.loadRecentlyViewed.bind(this);
    }

    connectedCallback() {
      if (!RecentlyViewed.hasLocalStorage()) {
        this.hidden = true;
        return;
      }

      if (this.dataset.productId) {
        RecentlyViewed.record(this.dataset.productId);
      }

      if (this._loaded || !this.dataset.url) return;

      if (window.Shopify?.designMode) {
        this.loadRecentlyViewed();
        return;
      }

      if ('IntersectionObserver' in window) {
        this._observer = getObserver({ rootMargin: '600px 0px', threshold: 0 }, true);
        this._observer.observe(this, this.loadRecentlyViewed);
      } else {
        this.loadRecentlyViewed();
      }
    }

    disconnectedCallback() {
      this._observer?.unobserve(this);
      this._observer = null;
    }

    static hasLocalStorage() {
      if (window.theme?.config?.hasLocalStorage === false) return false;
      if (window.theme?.config?.hasLocalStorage === true) return true;

      try {
        const key = '__storage_test__';
        window.localStorage.setItem(key, key);
        window.localStorage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    }

    static getStorageKey() {
      const themeName = window.theme?.settings?.themeName || 'SKY';
      return `${themeName}:recently-viewed`;
    }

    static readItems() {
      try {
        return JSON.parse(window.localStorage.getItem(RecentlyViewed.getStorageKey()) || '[]')
          .map((item) => parseInt(item, 10))
          .filter((item) => !Number.isNaN(item));
      } catch {
        return [];
      }
    }

    static writeItems(items) {
      window.localStorage.setItem(
        RecentlyViewed.getStorageKey(),
        JSON.stringify(items.slice(0, RecentlyViewed.MAX_ITEMS)),
      );
    }

    /** Newest first. Caps at MAX_ITEMS — adding a 6th drops the oldest. */
    static record(productId) {
      if (!RecentlyViewed.hasLocalStorage() || !productId) return;

      const id = parseInt(productId, 10);
      if (Number.isNaN(id)) return;

      const items = RecentlyViewed.readItems().filter((item) => item !== id);
      items.unshift(id);
      RecentlyViewed.writeItems(items);
    }

    getQueryString() {
      const excludeId = this.dataset.productId
        ? parseInt(this.dataset.productId, 10)
        : null;
      const limit = Math.min(
        parseInt(this.dataset.limit || RecentlyViewed.MAX_ITEMS, 10) || RecentlyViewed.MAX_ITEMS,
        RecentlyViewed.MAX_ITEMS,
      );

      return RecentlyViewed.readItems()
        .filter((item) => item !== excludeId)
        .slice(0, limit)
        .map((item) => `id:${item}`)
        .join(' OR ');
    }

    loadRecentlyViewed() {
      if (this._loaded || !this.dataset.url) return;

      const query = this.getQueryString();
      if (!query) {
        if (!window.Shopify?.designMode) {
          this.closest('.shopify-section')?.remove();
        }
        this.dispatchEvent(new CustomEvent('is-empty', { bubbles: true }));
        return;
      }

      this._loaded = true;

      fetch(`${this.dataset.url}${encodeURIComponent(query)}`)
        .then((response) => {
          if (!response.ok) throw new Error(`Recently viewed fetch failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          const sectionInnerHTML = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .querySelector('.shopify-section, [id^="shopify-section-"]');

          if (!sectionInnerHTML) {
            this._loaded = false;
            return;
          }

          const recentlyViewed = sectionInnerHTML.querySelector('recently-viewed');
          const sourceGrid = recentlyViewed?.querySelector('.products-list-wrapper');
          const targetGrid = this.querySelector('.products-list-wrapper');
          const hasProducts = Boolean(sourceGrid?.querySelector('[data-item], .product-card'));

          if (hasProducts && sourceGrid && targetGrid) {
            targetGrid.innerHTML = sourceGrid.innerHTML;
            this.hidden = false;
            this.dispatchEvent(new CustomEvent('recentlyViewed:loaded', { bubbles: true }));
            return;
          }

          if (hasProducts && recentlyViewed?.innerHTML.trim().length) {
            this.innerHTML = recentlyViewed.innerHTML;
            this.hidden = false;
            this.dispatchEvent(new CustomEvent('recentlyViewed:loaded', { bubbles: true }));
            return;
          }

          if (!window.Shopify?.designMode) {
            this.closest('.shopify-section')?.remove();
            this.dispatchEvent(new CustomEvent('is-empty', { bubbles: true }));
          }
        })
        .catch((error) => {
          this._loaded = false;
          console.error(error);
        });
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);

  const currentProductId = document.querySelector('buy-box[data-product-id]')?.dataset.productId;
  if (currentProductId) {
    RecentlyViewed.record(currentProductId);
  }
}

/**
 * Vertical swipe / pan on drawer chrome (header / drag handle).
 * Usage: <div class="drawer-header" is="swipe-gesture">…</div>
 * Swipe down closes the nearest open drawer via its close control / closeDrawer().
 * Events: gesture:panstart | panmove | panend | swipeup | swipedown | longpress
 */
export class SwipeGesture extends HTMLDivElement {
  constructor() {
    super();

    this._listeners = [];
    this.initialized = false;
    this.config = {
      thresholdY: 48,
      velocityThreshold: 0.4,
      disregardVelocityThresholdY: 80,
      pressThreshold: 8,
      longPressTime: 500,
    };

    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.dismissDrawer = this.dismissDrawer.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.teardown();
    this.initialized = false;
  }

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this._listeners.push({ target, type, handler, options });
  }

  teardown() {
    clearTimeout(this.longPressTimer);
    this._listeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this._listeners = [];
  }

  init() {
    if (this.initialized) return;

    this.initialized = true;
    const options = { passive: true };

    this.on(this, 'touchstart', this.onTouchStart, options);
    this.on(this, 'touchmove', this.onTouchMove, options);
    this.on(this, 'touchend', this.onTouchEnd, options);
    this.on(this, 'touchcancel', this.onTouchEnd, options);
    this.on(this, 'gesture:swipedown', this.dismissDrawer);
  }

  get drawer() {
    return this.closest('[data-drawer], [data-popup], .drawer');
  }

  dismissDrawer() {
    const drawer = this.drawer;
    if (!drawer?.hasAttribute('active')) return;

    // Prefer close button so drawer-specific handlers (unlock scroll, trapFocus) run.
    const closeBtn =
      drawer.querySelector('.drawer-close-btn[is="close-drawer"]') ||
      drawer.querySelector('[is="close-drawer"]:not(.fixed-overlay):not([data-fixed-overlay])') ||
      drawer.querySelector('[is="close-drawer"]');

    if (closeBtn) {
      closeBtn.click();
      return;
    }

    if (typeof drawer.close === 'function') {
      drawer.close();
      return;
    }

    closeDrawer(drawer);
  }

  fire(type, event) {
    this.dispatchEvent(
      new CustomEvent(`gesture:${type}`, {
        bubbles: true,
        composed: true,
        detail: {
          originalEvent: event,
          deltaY: this.touchMoveY ?? 0,
          velocityY: this.velocityY ?? 0,
        },
      })
    );
  }

  onTouchStart(event) {
    const touch = event.touches?.[0] || event.changedTouches?.[0];
    if (!touch) return;

    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY = Math.max(
      this.config.disregardVelocityThresholdY,
      Math.floor(0.5 * this.clientHeight) || 0
    );
    this.touchStartY = touch.screenY;
    this.touchStartTime = Date.now();
    this.touchMoveY = 0;
    this.touchEndY = null;
    this.velocityY = 0;
    this.swipingDirection = null;
    this.tracking = true;

    clearTimeout(this.longPressTimer);
    this.longPressTimer = setTimeout(() => this.fire('longpress', event), this.config.longPressTime);
    this.fire('panstart', event);
  }

  onTouchMove(event) {
    if (!this.tracking) return;

    const touch = event.touches?.[0] || event.changedTouches?.[0];
    if (!touch) return;

    this.touchMoveY = touch.screenY - (this.touchStartY ?? 0);
    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical';

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer);
    }

    this.fire('panmove', event);
  }

  onTouchEnd(event) {
    if (!this.tracking) return;
    this.tracking = false;

    const touch = event.changedTouches?.[0];
    this.touchEndY = touch ? touch.screenY : this.touchStartY;
    this.fire('panend', event);
    clearTimeout(this.longPressTimer);

    const y = (this.touchEndY ?? 0) - (this.touchStartY ?? 0);
    const absY = Math.abs(y);
    const elapsed = Math.max(1, Date.now() - (this.touchStartTime ?? Date.now()));
    this.velocityY = y / elapsed;

    if (absY <= this.thresholdY) return;

    if (y < 0) {
      if (this.velocityY < -this.config.velocityThreshold || y < -this.disregardVelocityThresholdY) {
        this.fire('swipeup', event);
      }
      return;
    }

    if (this.velocityY > this.config.velocityThreshold || y > this.disregardVelocityThresholdY) {
      this.fire('swipedown', event);
    }
  }
}
if (!customElements.get('swipe-gesture')) {
  customElements.define('swipe-gesture', SwipeGesture, { extends: 'div' });
}